import React, { useEffect, useState } from 'react';
import Lightbox from 'react-image-lightbox';
// Components
import CustomButton from 'components/common/CustomButton';
import InventoryTable from './InventoryTable';
import { default as ReadOnlyInventoryTable } from './noDeviationComponents/InventoryTable';
import FavoriteFlowers from './FavoriteFlowers';
import GuidelineVideoModal from './GuidelineVideoModal';
import TaskActions from './TaskActions';
import RightCaret from 'assets/images/svg/RightCaret';
// Helpers
import { preparationTypes } from 'constants/Helpers';
// API
import OrderDetailsService from 'services/OrderDetailsService';

/**
 * One task of the order: a collapsed row with the SKU, name and thumbnail, expanding
 * to the inventory table. The thumbnail opens the product gallery.
 */
const TaskAccordionItem = ({
  cartItem,
  orderMapId,
  orderId,
  preparationStatus,
  hasSubmittedBefore,
  hasDeviation,
  setHasSubmittedBefore,
  setHasDeviation,
  taskIndex,
  refreshCartDetails,
  task,
  isTaskBusy,
  onTaskAction,
  isOpen,
  onToggle,
  isGrouped = false,
  deviationEnabled = true,
}) => {
  const [inventoryDetails, setInventoryDetails] = useState([]);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(-1);

  const images = (inventoryDetails?.productImages ?? [])
    .map((img) => img?.image)
    .filter(Boolean);
  const thumbnail = images[0] ?? cartItem?.productImageFullPath;
  const prepType = preparationTypes.find(
    (pt) => parseInt(pt.id, 10) === parseInt(cartItem?.preparationType, 10)
  );

  const fetchCartDetails = async () => {
    try {
      // The read-only variant has always called this with three segments.
      const res = deviationEnabled
        ? await OrderDetailsService.getInventoryDetails(
            orderMapId,
            cartItem?.productId,
            cartItem?.id,
            orderId
          )
        : await OrderDetailsService.getInventoryDetails(
            orderMapId,
            cartItem?.productId,
            cartItem?.id
          );
      setInventoryDetails(res?.data);
    } catch (err) {
      // @TODO - Add datadog
    }
  };

  useEffect(() => {
    fetchCartDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const moveLightbox = (step) =>
    setLightboxIndex((prev) => (prev + step + images.length) % images.length);

  // Sits on the Submit row, opposite the Submit button. Without a deviation table there
  // is no such row, so it falls back to the task footer.
  const videoButton = cartItem?.fulfilmentVideoUrl ? (
    <CustomButton
      id="watch-guideline-video"
      name="Watch guideline video"
      handleClick={() => setIsVideoModalOpen(true)}
      disabled={false}
      type="watch-video"
      cssClasses="watch-video-btn"
    />
  ) : null;

  return (
    <div className={`task-item${isOpen ? ' open' : ''}`}>
      <div className="task-item-head">
        <span className="task-item-step">
          <span className={`task-step-badge${task ? ` status-${task.status}` : ''}`}>
            {taskIndex}
          </span>
        </span>

        {thumbnail && (
          <img
            className="task-item-thumb"
            src={thumbnail}
            alt={cartItem?.proName}
            onClick={() => setLightboxIndex(0)}
          />
        )}

        <button type="button" className="task-item-summary" onClick={onToggle}>
          <span className="task-item-text">
            <span className="task-item-title">
              {cartItem?.qty}X — {cartItem?.productSku}
            </span>
            <span className="task-item-name">{cartItem?.proName}</span>
            {/* Server-composed, shown verbatim. Inside a bundle group it only repeats the
                header's "Part of: <bundle>", so it is left out there. */}
            {!isGrouped && task?.customisationSummary && (
              <span className="task-item-note">{task.customisationSummary}</span>
            )}
          </span>

          <span className="task-item-tags">
            {cartItem?.productTierName && (
              <span className="task-tag tier">{cartItem.productTierName}</span>
            )}
            {prepType && (
              <span
                className="task-tag prep-type"
                style={{ backgroundColor: prepType.bgColor, color: prepType.textColor }}
              >
                {prepType.name}
              </span>
            )}

            <TaskActions
              task={task}
              isBusy={isTaskBusy}
              onStart={() => onTaskAction('start', task)}
              onPause={() => onTaskAction('pause', task)}
              onResume={() => onTaskAction('resume', task)}
              onFinish={() => onTaskAction('finish', task)}
            />
          </span>

          <RightCaret />
        </button>
      </div>

      {/* Kept mounted when collapsed: InventoryTable is what reports deviations back
          up, and the Done button is gated on them. */}
      <div className={`task-item-body${isOpen ? '' : ' collapsed'}`}>
          {cartItem?.cakeMessage && (
            <div className="task-item-meta">
              <p>
                <b>Personalized msg:</b> {cartItem.cakeMessage}
              </p>
            </div>
          )}

          {deviationEnabled ? (
            <div className="productDetails-right-col">
              <div className="productDetails-right-header">
                <table className="table">
                  <thead>
                    <tr>
                      <th scope="col">Item Image</th>
                      <th scope="col">Name</th>
                      <th scope="col">Barcode</th>
                      <th scope="col">Qty</th>
                      <th scope="col">Used Qty</th>
                      <th scope="col">Damaged Qty</th>
                      <th scope="col">Damaged Image</th>
                    </tr>
                  </thead>
                </table>
              </div>
              <InventoryTable
                inventoryData={inventoryDetails?.inventoryDto}
                orderMapId={orderMapId}
                preparationStatus={preparationStatus}
                hasSubmittedBefore={hasSubmittedBefore}
                hasDeviation={hasDeviation}
                setHasSubmittedBefore={setHasSubmittedBefore}
                setHasDeviation={setHasDeviation}
                cartQty={cartItem?.qty}
                fetchCartDetails={fetchCartDetails}
                cartId={cartItem?.id}
                leadingAction={videoButton}
              />
            </div>
          ) : (
            <ReadOnlyInventoryTable inventoryData={inventoryDetails?.inventoryDto} />
          )}


        <div className="task-item-footer">
          {!deviationEnabled && videoButton}
          <FavoriteFlowers cartId={cartItem?.id} />
        </div>
      </div>

      {isVideoModalOpen && (
        <GuidelineVideoModal
          isModalOpen={isVideoModalOpen}
          setIsOpenModal={setIsVideoModalOpen}
          videoUrl={cartItem?.fulfilmentVideoUrl}
        />
      )}

      {lightboxIndex > -1 && images.length > 0 && (
        <Lightbox
          mainSrc={images[lightboxIndex]}
          nextSrc={images.length > 1 ? images[(lightboxIndex + 1) % images.length] : undefined}
          prevSrc={
            images.length > 1
              ? images[(lightboxIndex + images.length - 1) % images.length]
              : undefined
          }
          onCloseRequest={() => setLightboxIndex(-1)}
          onMoveNextRequest={() => moveLightbox(1)}
          onMovePrevRequest={() => moveLightbox(-1)}
        />
      )}
    </div>
  );
};

export default TaskAccordionItem;
